/**
 * The high-score tables, persisted (docs/spec/07 §2). A player sees their own tables merged with
 * the world's (the server's, as last fetched). A score entered here goes into their own table at
 * once and into an outbox that `ScoreSync` empties into the world tables, retrying until it can.
 */

import type { ScoreEntry, TableId, Tables } from '../game/hiscores';
import { insertScore, mergeTables, parseEntries, parseTables, parseWorldTables, rankFor, SCORE_ID, TABLE_IDS } from '../game/hiscores';
import type { KeyValueStore } from './storage';
import { KEYS } from './storage';

/** A score waiting to be sent: its table, the entry with its id, and the game log that proves it. */
export interface Outgoing {
  readonly table: TableId;
  readonly entry: ScoreEntry & { readonly id: string };
  /** Which player of the game it was (0 or 1). */
  readonly player: number;
  /** The game's log (game/gameLog): the server replays it to check the score. */
  readonly log: string;
}

/** A random score id (see `SCORE_ID`). */
export function newScoreId(): string {
  const bytes = new Uint8Array(10);
  crypto.getRandomValues(bytes);
  return [...bytes].map((b) => b.toString(36).padStart(2, '0')).join('').slice(0, 16);
}

function parseOutbox(json: string | null): Outgoing[] {
  try {
    const raw: unknown = JSON.parse(json ?? '[]');
    if (!Array.isArray(raw)) return [];
    const out: Outgoing[] = [];
    for (const item of raw as { table?: unknown; entry?: unknown; player?: unknown; log?: unknown }[]) {
      const table = TABLE_IDS.find((t) => t === item?.table);
      const [entry] = parseEntries([item?.entry]);
      const player = item?.player === 1 ? 1 : 0;
      if (table && entry?.id !== undefined && SCORE_ID.test(entry.id) && typeof item?.log === 'string') {
        out.push({ table, entry: { ...entry, id: entry.id }, player, log: item.log });
      }
    }
    return out;
  } catch {
    return [];
  }
}

export class ScoreBook {
  private own: Tables;
  private world: Tables;
  private shown: Tables;
  private outbox: Outgoing[];
  private readonly listeners: (() => void)[] = [];

  constructor(private readonly store: KeyValueStore, private readonly makeId: () => string = newScoreId) {
    this.own = parseTables(store.load(KEYS.scores));
    this.world = parseWorldTables(store.load(KEYS.worldScores));
    this.shown = mergeTables(this.own, this.world);
    this.outbox = parseOutbox(store.load(KEYS.outbox));
  }

  /** The tables as the player sees them: their own and the world's. */
  all(): Tables {
    return this.shown;
  }

  table(id: TableId): readonly ScoreEntry[] {
    return this.shown[id];
  }

  qualifies(id: TableId, score: number): boolean {
    return rankFor(this.shown[id], score) >= 0;
  }

  /**
   * Add an entry; its rank (0-based) in the tables as shown, or -1 if it did not make them. With a
   * proof (the game's log and which player) it is also queued for the world tables.
   */
  submit(id: TableId, entry: ScoreEntry, proof?: { readonly log: string; readonly player: number }): number {
    if (!this.qualifies(id, entry.score)) return -1;
    const mine = { ...entry, id: this.makeId() };
    const { tables, rank } = insertScore(this.own, id, mine);
    if (rank < 0) return -1;
    this.own = tables;
    this.store.save(KEYS.scores, JSON.stringify(tables));
    this.shown = mergeTables(this.own, this.world);
    if (proof) {
      this.outbox = [...this.outbox, { table: id, entry: mine, player: proof.player, log: proof.log }];
      this.saveOutbox();
      this.listeners.forEach((fn) => fn());
    }
    return this.shown[id].findIndex((e) => e.id === mine.id);
  }

  /** Called when a score is entered, so it can be sent. */
  onSubmit(fn: () => void): void {
    this.listeners.push(fn);
  }

  /** Scores the server has not taken yet, oldest first. */
  pending(): readonly Outgoing[] {
    return this.outbox;
  }

  /** The server took (or for good refused) this score: stop sending it. */
  settled(id: string): void {
    this.outbox = this.outbox.filter((o) => o.entry.id !== id);
    this.saveOutbox();
  }

  /** New world tables from the server. */
  setWorld(world: Tables): void {
    this.world = world;
    this.store.save(KEYS.worldScores, JSON.stringify(world));
    this.shown = mergeTables(this.own, this.world);
  }

  private saveOutbox(): void {
    if (this.outbox.length > 0) this.store.save(KEYS.outbox, JSON.stringify(this.outbox));
    else this.store.remove(KEYS.outbox);
  }
}
